import axios from "axios"

// axios configuration (can be used for global settings)
axios.defaults.headers.common['Content-Type'] = 'application/json';

/**
 * Fetches the list of bots allowed for a specific user.
 * @param {string} userId - The unique identifier of the user.
 * @returns {Promise<any>} A promise that resolves to the user's allowed bots data.
 */
export const fetchUserBots = async (userId: string) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/users/${userId}/bots`;
        const result = await axios.get(endpoint, options);
        //console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error fetching allowed bots:', error);
        throw error;
    }
}

/**
 * Sends a chat message to the backend.
 * @param {string} message - The message text to send.
 * @param {string | null} sessionId - The current session ID, if any.
 * @param {string | null} agentId - The ID of the agent being messaged.
 * @returns {Promise<any>} A promise that resolves to the chat response.
 */
export const sendMessage = async (message: string, sessionId: string | null, agentId: string | null) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + '/api/chat';
        const payload = { message, sessionId, agentId };
        const result = await axios.post(endpoint, payload, options);
        //console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
}

/**
 * Retrieves the chat history for a specific session.
 * @param {string} sessionId - The unique identifier for the chat session.
 * @returns {Promise<any>} A promise that resolves to the session's message history.
 */
export const getChatHistory = async (sessionId: string) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/chat/history/${sessionId}`;
        const result = await axios.get(endpoint, options);
        //console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error fetching chat history:', error);
        throw error;
    }
}

/**
 * Fetches all conversation IDs for the current user.
 * @returns {Promise<any>} A promise that resolves to a list of conversation sessions.
 */
export const getConversationIds = async () => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/chat/conversations`;
        const result = await axios.get(endpoint, options);
        //console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error fetching conversation IDs:', error);
        throw error;
    }
}

/**
 * Searches conversation history for sessions containing messages matching the query.
 * @param {string} query - The search string (min 2 chars).
 * @returns {Promise<any>} A promise that resolves to an array of matching sessions with snippets.
 */
export const searchConversations = async (query: string) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/chat/search?q=${encodeURIComponent(query)}`;
        const result = await axios.get(endpoint, options);
        return result.data;
    }
    catch (error) {
        console.error('Error searching conversations:', error);
        throw error;
    }
}

/**
 * Updates the chat history (Placeholder).
 */
export const updateChatHistory = async () => {

}

/**
 * Deletes the chat history for a specific session.
 * @param {string} sessionId - The unique identifier for the chat session to delete.
 * @returns {Promise<any>} A promise that resolves when the deletion is complete.
 */
export const deleteChatHistory = async (sessionId: string) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/chat/history/${sessionId}`;
        const result = await axios.delete(endpoint, options);
        console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error deleting chat history:', error);
        throw error;
    }
}

/**
 * Deletes all chat history for the current user.
 * @returns {Promise<any>} A promise that resolves when the deletion is complete.
 */
export const deleteAllChatHistory = async () => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/chat/history`;
        const result = await axios.delete(endpoint, options);
        console.log(result.data);
        return result.data;
    }
    catch (error) {
        console.error('Error deleting all chat history:', error);
        throw error;
    }
}

/**
 * Sends feedback for a specific message in a session.
 * @param {string} sessionId - The unique identifier for the chat session.
 * @param {string} agentId - The ID of the agent that sent the message.
 * @param {number} messageIndex - The index of the message in the conversation.
 * @param {'thumbs_up' | 'thumbs_down'} feedback - The type of feedback (thumbs up or thumbs down).
 * @param {string | null} idealAnswer - The user's suggested ideal answer.
 * @param {string | null} comment - Additional feedback comments.
 * @param {string[] | null} reasons - A list of reasons for the feedback (typically for negative feedback).
 * @returns {Promise<any>} A promise that resolves when the feedback is sent.
 */
export const sendFeedback = async (sessionId: string, agentId: string, messageIndex: number, feedback: 'thumbs_up' | 'thumbs_down', idealAnswer: string | null = null, comment: string | null = null, reasons: string[] | null = null) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + '/api/chat/feedback';
        const payload = { sessionId, agentId, messageIndex, feedback, idealAnswer, comment, reasons };
        const result = await axios.post(endpoint, payload, options);
        return result.data;
    }
    catch (error) {
        console.error('Error sending feedback:', error);
        throw error;
    }
}

/**
 * Initiates the login process by fetching the OIDC provider's authorization URL.
 * @returns {Promise<{redirectUrl: string}>} The redirection URL.
 */
export const login = async() => {
    try {
        const base = '';
        const endpoint = base + `/api/auth/login`;
        const result = await axios.get(endpoint);
        return result.data;
    }
    catch (error) {
        console.error('Error initiating login:', error);
        throw error;
    }
}

/**
 * Initiates the logout process by fetching the OIDC provider's logout URL.
 * @returns {Promise<{redirectUrl: string}>} The redirection URL.
 */
export const logout = async() => {
    try {
        const base = '';
        const endpoint = base + `/api/auth/logout`;
        const result = await axios.get(endpoint);
        return result.data;
    }
    catch (error) {
        console.error('Error initiating logout:', error);
        throw error;
    }
}

/**
 * Updates the user's theme preference in the backend.
 * @param {string} userId - The unique identifier of the user.
 * @param {string} theme - The theme preference ('light', 'dark', or 'system').
 * @returns {Promise<any>} A promise that resolves when the update is complete.
 */
export const updateUserTheme = async (userId: string, theme: string) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/users/${userId}/theme`;
        const payload = { theme };
        const result = await axios.put(endpoint, payload, options);
        return result.data;
    }
    catch (error) {
        console.error('Error updating user theme:', error);
        throw error;
    }
}

/**
 * Updates the user's auto-delete preference in the backend.
 * @param {string} userId - The unique identifier of the user.
 * @param {boolean} autoDelete - Whether to automatically delete history older than 30 days.
 * @returns {Promise<any>} A promise that resolves when the update is complete.
 */
export const updateUserAutoDelete = async (userId: string, autoDelete: boolean) => {
    try {
        const base = '';
        const options = {
            headers: {
                'Content-Type': 'application/json',
            },
        }
        const endpoint = base + `/api/users/${userId}/auto-delete`;
        const payload = { autoDelete };
        const result = await axios.put(endpoint, payload, options);
        return result.data;
    }
    catch (error) {
        console.error('Error updating user auto-delete preference:', error);
        throw error;
    }
}

/**
 * Fetches the currently authenticated user's information.
 * @returns {Promise<any>} A promise that resolves to the authenticated user's data.
 */
export const fetchUser = async () => {
    try {
        const base = '';
        const endpoint = base + `/api/auth/user`;
        const result = await axios.get(endpoint);
        return result.data
    }
    catch (error) {
        // Handle unauthorized or unauthenticated cases specifically if needed
        console.error('Error fetching user:', error);
        throw error;
    }
}
